import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryImageMap: Record<string, string> = {
  anatomy: 'https://readdy.ai/api/search-image?query=Scientific%20medical%20illustration%20of%20male%20pelvic%20anatomy%20diagram%20with%20prostate%20focus%2C%20clean%20educational%20style%20with%20warm%20neutral%20background%2C%20professional%20medical%20aesthetic&width=800&height=400&seq=cat-anatomy&orientation=landscape',
  techniques: 'https://readdy.ai/api/search-image?query=Gentle%20illustrated%20guide%20concept%20for%20health%20techniques%2C%20clean%20educational%20style%20with%20warm%20neutral%20background%2C%20professional%20medical%20instruction%20aesthetic&width=800&height=400&seq=cat-techniques&orientation=landscape',
  'health-benefits': 'https://readdy.ai/api/search-image?query=Abstract%20health%20benefits%20and%20wellness%20concept%20with%20warm%20earthy%20tones%2C%20clean%20modern%20medical%20research%20aesthetic%2C%20professional%20healthcare%20illustration&width=800&height=400&seq=cat-health&orientation=landscape',
  safety: 'https://readdy.ai/api/search-image?query=Medical%20safety%20and%20protection%20concept%20with%20shield%20symbol%2C%20clean%20warm%20professional%20healthcare%20aesthetic%2C%20educational%20safety%20guide%20illustration&width=800&height=400&seq=cat-safety&orientation=landscape',
  faq: 'https://readdy.ai/api/search-image?query=Friendly%20question%20and%20answer%20health%20education%20concept%2C%20clean%20warm%20approachable%20medical%20communication%20style%2C%20professional%20yet%20welcoming%20illustration&width=800&height=400&seq=cat-faq&orientation=landscape',
};

const articleImageUrls: Record<string, string> = {
  'prostate-anatomy-basics': 'https://readdy.ai/api/search-image?query=Scientific%20medical%20illustration%20of%20male%20pelvic%20anatomy%20showing%20prostate%20gland%20position%2C%20clean%20educational%20diagram%20style%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-prostate-anatomy-basics&orientation=landscape',
  'prostate-nerve-system': 'https://readdy.ai/api/search-image?query=Scientific%20anatomical%20illustration%20of%20male%20pelvic%20nerve%20network%2C%20clean%20educational%20medical%20style%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-prostate-nerve-system&orientation=landscape',
  'hormones-and-prostate': 'https://readdy.ai/api/search-image?query=Scientific%20medical%20illustration%20of%20hormone%20molecular%20structure%2C%20clean%20educational%20style%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-hormones-and-prostate&orientation=landscape',
  'prostate-and-sexual-response': 'https://readdy.ai/api/search-image?query=Scientific%20illustration%20of%20male%20sexual%20response%20cycle%20phases%2C%20clean%20medical%20educational%20diagram%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-prostate-and-sexual-response&orientation=landscape',
  'external-prostate-massage': 'https://readdy.ai/api/search-image?query=Gentle%20illustration%20of%20perineum%20massage%20technique%2C%20clean%20soft%20educational%20style%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-external-prostate-massage&orientation=landscape',
  'internal-prostate-massage': 'https://readdy.ai/api/search-image?query=Anatomical%20diagram%20showing%20internal%20prostate%20massage%20technique%2C%20clean%20educational%20style%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-internal-prostate-massage&orientation=landscape',
  'prostate-massage-tools': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20product%20photography%20of%20medical%20grade%20silicone%20devices%2C%20professional%20health%20product%20display&width=400&height=300&seq=cat-prostate-massage-tools&orientation=landscape',
  'breathing-and-relaxation': 'https://readdy.ai/api/search-image?query=Serene%20meditation%20scene%20with%20soft%20warm%20lighting%2C%20abstract%20anatomical%20illustration%20of%20diaphragmatic%20breathing&width=400&height=300&seq=cat-breathing-and-relaxation&orientation=landscape',
  'partner-assisted-techniques': 'https://readdy.ai/api/search-image?query=Warm%20intimate%20illustration%20of%20couple%20communication%20and%20trust%2C%20soft%20warm%20lighting%2C%20emotional%20connection&width=400&height=300&seq=cat-partner-assisted-techniques&orientation=landscape',
  'prostate-massage-health': 'https://readdy.ai/api/search-image?query=Scientific%20medical%20research%20illustration%20with%20abstract%20data%20visualization%2C%20clean%20professional%20academic%20style&width=400&height=300&seq=cat-prostate-massage-health&orientation=landscape',
  'prostate-orgasm-science': 'https://readdy.ai/api/search-image?query=Abstract%20scientific%20visualization%20of%20brain%20neural%20networks%20with%20warm%20color%20gradients%2C%20clean%20modern%20neuroscience&width=400&height=300&seq=cat-prostate-orgasm-science&orientation=landscape',
  'prostate-health-aging': 'https://readdy.ai/api/search-image?query=Healthy%20aging%20lifestyle%20concept%20with%20warm%20earthy%20tones%2C%20natural%20foods%20and%20exercise%20elements&width=400&height=300&seq=cat-prostate-health-aging&orientation=landscape',
  'mental-health-benefits': 'https://readdy.ai/api/search-image?query=Calm%20mindfulness%20meditation%20concept%20with%20abstract%20mental%20wellness%20visualization%2C%20warm%20soft%20colors&width=400&height=300&seq=cat-mental-health-benefits&orientation=landscape',
  'safety-basics': 'https://readdy.ai/api/search-image?query=Clean%20medical%20safety%20concept%20illustration%20with%20shield%20and%20protective%20elements%2C%20warm%20neutral%20professional%20healthcare&width=400&height=300&seq=cat-safety-basics&orientation=landscape',
  'common-mistakes': 'https://readdy.ai/api/search-image?query=Educational%20illustration%20showing%20cross%20marks%20on%20incorrect%20practices%2C%20clean%20instructional%20design%2C%20warm%20neutral%20background&width=400&height=300&seq=cat-common-mistakes&orientation=landscape',
  'medical-consultation': 'https://readdy.ai/api/search-image?query=Professional%20medical%20consultation%20scene%20with%20warm%20clinical%20setting%2C%20doctor%20patient%20communication&width=400&height=300&seq=cat-medical-consultation&orientation=landscape',
  'what-is-prostate-orgasm': 'https://readdy.ai/api/search-image?query=Friendly%20health%20education%20illustration%20with%20question%20marks%2C%20clean%20warm%20approachable%20medical%20communication&width=400&height=300&seq=cat-what-is-prostate-orgasm&orientation=landscape',
  'first-time-guide': 'https://readdy.ai/api/search-image?query=Gentle%20beginner%20friendly%20health%20education%20illustration%20with%20step%20by%20step%20guide%2C%20clean%20warm%20encouraging&width=400&height=300&seq=cat-first-time-guide&orientation=landscape',
  'prostate-health-myths': 'https://readdy.ai/api/search-image?query=Educational%20illustration%20showing%20myth%20versus%20truth%20concept%20with%20light%20bulb%2C%20clean%20warm%20informative%20medical&width=400&height=300&seq=cat-prostate-health-myths&orientation=landscape',
  'diet-and-prostate': 'https://readdy.ai/api/search-image?query=Healthy%20food%20arrangement%20with%20tomatoes%20green%20vegetables%20fish%20on%20warm%20wooden%20table%2C%20clean%20nutrition%20lifestyle&width=400&height=300&seq=cat-diet-and-prostate&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.description,
    url: `https://prostate-health-guide.readdy.ai/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          name: a.title,
          url: `https://prostate-health-guide.readdy.ai/article/${a.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`${category.name},前列腺健康,男性健康科普`}
        canonicalUrl={`https://prostate-health-guide.readdy.ai/category/${slug}`}
        ogType="website"
        schema={catSchema}
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        <div className="relative w-full h-[200px] md:h-[280px] overflow-hidden">
          <img
            src={categoryImageMap[slug] || categoryImageMap.anatomy}
            alt={category.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center">
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 mx-auto mb-3">
                <i className={`${category.icon} text-xl md:text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-2">
                {category.name}
              </h1>
              <p className="text-sm text-background-50/80 max-w-lg mx-auto">
                {category.description}
              </p>
            </div>
          </div>
        </div>

        <div className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="sm:w-48 h-36 sm:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={articleImageUrls[article.slug] || articleImageUrls['prostate-anatomy-basics']}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <h2 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span>{article.readTime} 分钟阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>

        <div className="py-10 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">其他分类</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories
                .filter((c) => c.slug !== slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className="block bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-200 transition-colors cursor-pointer text-center"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mx-auto mb-2">
                      <i className={`${c.icon} text-sm`}></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-medium whitespace-nowrap">
                      {c.name}
                    </span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}